import { type FC, type UIEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import { IconButton } from './IconButton';
import { PostMediaFrame, type PostMediaImage } from './PostMediaFrame';

interface PostMediaCarouselProps {
  images: PostMediaImage[];
}

const getSlideStep = (track: HTMLDivElement) => {
  const [firstSlide, secondSlide] = track.children;
  return secondSlide ? secondSlide.getBoundingClientRect().left - firstSlide.getBoundingClientRect().left : 0;
};

export const PostMediaCarousel: FC<PostMediaCarouselProps> = ({ images }) => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const lastIndex = images.length - 1;

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const track = event.currentTarget;
    const slideStep = getSlideStep(track);
    if (slideStep > 0) setActiveIndex(Math.min(lastIndex, Math.max(0, Math.round(track.scrollLeft / slideStep))));
  };

  const scrollToImage = (index: number) => {
    const track = trackRef.current;
    if (track) track.scrollTo({ left: index * getSlideStep(track), behavior: 'smooth' });
  };

  const isFirstImage = activeIndex === 0;
  const isLastImage = activeIndex === lastIndex;

  return (
    <div className="my-3" data-testid="post-media-carousel">
      <div className="relative">
        <div
          ref={trackRef}
          data-testid="post-media-carousel-track"
          onScroll={handleScroll}
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory rounded-lg [scrollbar-width:none]"
        >
          {images.map((image, index) => (
            <PostMediaFrame key={image.id} images={images} index={index} className="w-[70%] shrink-0 snap-start" />
          ))}
        </div>

        <div className="pointer-events-none absolute inset-0 flex items-center justify-between px-2">
          <IconButton
            aria-label={t('community.post.carousel.previous_image')}
            className={cn('pointer-events-auto bg-white/90 shadow-sm hover:bg-white', { 'opacity-30': isFirstImage })}
            disabled={isFirstImage}
            onClick={() => scrollToImage(activeIndex - 1)}
          >
            <ChevronLeftIcon className="size-4" />
          </IconButton>
          <IconButton
            aria-label={t('community.post.carousel.next_image')}
            className={cn('pointer-events-auto bg-white/90 shadow-sm hover:bg-white', { 'opacity-30': isLastImage })}
            disabled={isLastImage}
            onClick={() => scrollToImage(activeIndex + 1)}
          >
            <ChevronRightIcon className="size-4" />
          </IconButton>
        </div>
      </div>

      <div className="sr-only" aria-live="polite">
        {t('community.post.carousel.image_position', { current: activeIndex + 1, total: images.length })}
      </div>

      <div className="flex items-center justify-center gap-1.5 pt-2">
        {images.map((image, index) => (
          <button
            key={image.id}
            type="button"
            aria-label={t('community.post.carousel.go_to_image', { number: index + 1 })}
            aria-current={index === activeIndex ? 'true' : undefined}
            className={cn('size-1.5 rounded-full', {
              'bg-black': index === activeIndex,
              'bg-black/20': index !== activeIndex,
            })}
            onClick={() => scrollToImage(index)}
          />
        ))}
      </div>
    </div>
  );
};
